import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "./queryKeys";

export interface UnitDetail {
  id: string;
  unitId: string;
  alias: string;
  unitNumber: string;
  unitType: string;
  maxTenants: number;
  rentAmount: number;
  dueDate: number;
  securityDeposit: number | null;
  agreementEndDate: string | null;
  noticePeriodDays: number;
  notes: string | null;
  tenants: {
    id: string;
    name: string;
    phone: string;
    email: string | null;
  }[];
}

export interface PropertyDetail {
  id: string;
  name: string;
  address: string;
  latitude: number | null;
  longitude: number | null;
  totalRent: number;
  isArchived: boolean;
  createdAt: string;
  units: UnitDetail[];
}

export interface RentRecordDetail {
  id: string;
  roomId: string;
  unitId?: string;
  propertyName: string;
  roomAlias: string;
  roomNumber: string;
  unitAlias?: string;
  unitNumber?: string;
  roomDueDate: number;
  unitDueDate?: number;
  month: number;
  year: number;
  amount: number;
  status: string;
  paidDate: string | null;
  paymentMode: string | null;
  notes: string | null;
}

export interface ExpenseDetail {
  id: string;
  propertyId: string;
  unitId?: string | null;
  propertyName: string;
  unitAlias?: string | null;
  unitNumber?: string | null;
  category: string;
  amount: number;
  date: string;
  paidBy: string;
  notes: string | null;
}

async function fetchPropertyDetail(propertyId: string) {
  return api.get<PropertyDetail>(`/properties/${propertyId}`);
}

async function fetchPropertyRent(propertyId: string, year: number) {
  const res = await api.get<{ records: RentRecordDetail[] }>(
    `/rent?propertyId=${propertyId}&year=${year}`
  );
  return res.records;
}

async function fetchPropertyExpenses(
  propertyId: string,
  from: string,
  to: string
) {
  const res = await api.get<{ expenses: ExpenseDetail[] }>(
    `/expenses?propertyId=${propertyId}&from=${from}&to=${to}`
  );
  return res.expenses;
}

export function usePropertyDetailQuery(propertyId: string | null) {
  const now = new Date();
  const year = now.getFullYear();
  const from = `${year}-01-01T00:00:00.000Z`;
  const to = `${year}-12-31T23:59:59.999Z`;

  const propertyQuery = useQuery({
    queryKey: queryKeys.propertyDetail(propertyId),
    queryFn: () => fetchPropertyDetail(propertyId!),
    enabled: !!propertyId,
  });

  const rentQuery = useQuery({
    queryKey: queryKeys.propertyRent(propertyId ?? "", year),
    queryFn: () => fetchPropertyRent(propertyId!, year),
    enabled: !!propertyId,
  });

  const expensesQuery = useQuery({
    queryKey: queryKeys.propertyExpenses(propertyId ?? "", from, to),
    queryFn: () => fetchPropertyExpenses(propertyId!, from, to),
    enabled: !!propertyId,
  });

  const loading =
    propertyQuery.isLoading || rentQuery.isLoading || expensesQuery.isLoading;
  const error =
    propertyQuery.error ?? rentQuery.error ?? expensesQuery.error;
  const property = propertyQuery.data ?? null;
  const rentRecords = rentQuery.data ?? [];
  const expenses = expensesQuery.data ?? [];

  return {
    property,
    rentRecords,
    expenses,
    loading,
    error: error instanceof Error ? error.message : (error ? "Failed to load" : null),
    dataUpdatedAt: propertyQuery.dataUpdatedAt ?? 0,
    refetch: () => {
      propertyQuery.refetch();
      rentQuery.refetch();
      expensesQuery.refetch();
    },
  };
}
